import React from 'react';
import { BriefPage } from '../PartnershipBrief/BriefPage';
import { BriefHeader } from '../PartnershipBrief/BriefHeader';
import { useDocPaging } from '../../contexts/DocPagingContext';
import { usePricing } from '../../contexts/PricingContext';
import { roiMultiple } from '../../utils/roiMultiple';
import { getRampShares, getRevenueDelay } from '../../utils/roiCalculations';
import { clawbackMonths, commissionsModeled, firstYearAcv, managerView, overrideBonusMonth } from '../../utils/commissions';
import { FinancialModelData, ProjectionLine, compactUsd, count, pct, usd } from '../../utils/financialModel';
import { contributionNote, modelLead, modelLeadGoToMarket, stressDefinition } from '../../data/financialModel';
const label = 'text-[12px] font-bold uppercase tracking-[0.14em] text-[#4a5560]';
const darkLabel = 'text-[12px] font-bold uppercase tracking-[0.14em] text-[#aab4bc]';
type Column = {
  key: string;
  title: string;
  value: (line: ProjectionLine) => number;
  format: (v: number) => string;
  result?: boolean;
  groupStart?: boolean;
};

// Costs stay split so an ad spend ramp is visible month by month.
const COLUMNS: Column[] = [{
  key: 'hires',
  title: 'Hires',
  value: l => l.hires,
  format: count
}, {
  key: 'activeReps',
  title: 'Active reps',
  value: l => l.activeReps,
  format: count
}, {
  key: 'sales',
  title: 'Sales',
  value: l => l.sales,
  format: count,
  groupStart: true
}, {
  key: 'revenue',
  title: 'Revenue',
  value: l => l.revenue,
  format: usd
}, {
  key: 'grossProfit',
  title: 'Gross profit',
  value: l => l.grossProfit,
  format: usd
}, {
  key: 'hireFees',
  title: 'D2D HIRE fees',
  value: l => l.hireFees,
  format: usd,
  groupStart: true
}, {
  key: 'adSpend',
  title: 'Ad spend',
  value: l => l.adSpend,
  format: usd
}, {
  key: 'bonuses',
  title: 'Est. bonuses',
  value: l => l.bonuses,
  format: usd
}, {
  key: 'net',
  title: 'Contribution',
  value: l => l.net,
  format: usd,
  result: true,
  groupStart: true
}, {
  key: 'cumulative',
  title: 'Cumulative',
  value: l => l.cumulative,
  format: usd,
  result: true
}];
interface ModelSheetProps {
  model: FinancialModelData;
}

/**
 * The one-page financial model: the answer, then the month-by-month receipts,
 * then how one rep pays back and how the plan holds up under stress.
 */
export function ModelSheet({
  model
}: ModelSheetProps) {
  const {
    summary,
    projection,
    projectionYearOne,
    scenario,
    rep,
    inputs,
    chain
  } = model;
  const paging = useDocPaging();
  const {
    isGoToMarket
  } = usePricing();
  const lead = isGoToMarket ? modelLeadGoToMarket : modelLead;
  const scenarioActive = scenario.halfSales || scenario.doubleAttrition;
  const yearOne = rep?.horizons.find(h => h.key === '1y');
  const ninetyDays = rep?.horizons.find(h => h.key === '90d');
  const headline = summary.investment <= 0 ? 'The full cost and return, on your numbers.' : summary.net > 0 ? `${compactUsd(summary.net)} contribution after recruiting costs: a ${roiMultiple(summary.roi)} return in the first 12 months.` : `A ${roiMultiple(summary.roi)} return on recruiting spend in the first 12 months${summary.breakevenLabel ? `, breaking even in ${summary.breakevenLabel}` : ''}.`;
  const cell = (line: ProjectionLine, column: Column, isTotal = false) => {
    const value = column.value(line);
    // The first month's fees are collected in the Implementation row (signing).
    const prepaid = !isTotal && column.key === 'hireFees' && model.firstMonthPrepaid && line === projection[1] && line.hireFees === 0;
    const text = prepaid ? 'At signing' : isTotal && column.key === 'activeReps' ? `${column.format(value)} at end` : column.format(value);
    return <td key={column.key} className={`py-[5px] px-1.5 text-right whitespace-nowrap tabular-nums ${column.groupStart ? 'border-l border-[#d5dade]' : ''} ${column.result && value < 0 ? 'text-[#4a5560]' : ''} ${column.result && value >= 0 ? 'text-[#14684d] font-bold' : ''}`} data-id="element-479" data-mp="korrhs2copp1">
        {text}
      </td>;
  };
  const searchRange = model.leaderMonths > 1 ? `Months 1–${model.leaderMonths}` : 'Month 1';
  const campaignStart = model.leaderMonths + 1;
  const footnote = [
  // Leads the fine print: how sales are modeled, and why it's conservative.
  `Sales are modeled conservatively: only hires expected to reach day 90 produce sales, ramping ${getRampShares(inputs).map(share => Math.round(share * 100)).join('/')}% of veteran output in their first three months; reps who wash out are credited with none.`, model.firstMonthPrepaid ? `Implementation = signing, including Month 1's fees.` : null, model.leaderMonths ? `${searchRange} (leader): search fee and ads; the leader is hired in Month ${model.leaderMonths} and sells through year one. The rep campaign's ads go live in Month ${campaignStart}.` : null, 'Est. bonuses are billed the month after they are earned. Revenue is after pull-through and booked at sale; gross profit is revenue × margin.', getRevenueDelay(inputs) > 0 ? `Sales, revenue and profit are delayed ${getRevenueDelay(inputs)} month${getRevenueDelay(inputs) === 1 ? '' : 's'} to reflect timing before they land; costs are not delayed.` : null, contributionNote].filter(Boolean).join(' ');

  // With commissions modeled, gross profit is after commission cash flow:
  // upfront on first-year ACV at sale, clawed back on early cancels.
  const residualCommission = Number(inputs.residualCommissionPct) || 0;
  const backendCommission = Number(inputs.backendCommissionPct) || 0;
  const managerBonusMonth = overrideBonusMonth(inputs);
  const commissionNote = managerView(inputs) ? `Gross profit is the manager's override income: ${Number(inputs.overrideUpfrontPct) || 0}% of first-year ACV (${usd(firstYearAcv(inputs))}) at sale, plus ${Number(inputs.overrideBackendPct) || 0}% backend on accounts in good standing after ${clawbackMonths(inputs)} months, net of overrides clawed back on accounts that cancelled inside that window, paid ${managerBonusMonth ? `as a bonus in Month ${managerBonusMonth}` : 'as each window closes'}.` : commissionsModeled(inputs) ? `Gross profit is after commissions: ${Number(inputs.commissionPct) || 0}% of first-year ACV (${usd(firstYearAcv(inputs))}) paid at sale, clawed back if the customer cancels within ${clawbackMonths(inputs)} months${backendCommission > 0 ? `, then ${backendCommission}% backend on customers still active when that window closes` : ''}${residualCommission > 0 ? `, plus ${residualCommission}% of each recurring payment` : ''}.` : null;

  // Residual models compound: say so plainly under the table.
  const residualTicket = inputs.residualTicket ?? 0;
  const residualNote = residualTicket > 0 && (inputs.revenueCadence === 'monthly' || inputs.revenueCadence === 'quarterly') ? `${inputs.revenueCadence === 'monthly' ? 'Monthly' : 'Quarterly'} residual model: on top of each sale, every customer who stays pays ${usd(residualTicket)} ${inputs.revenueCadence === 'monthly' ? 'a month' : 'a quarter'}, so revenue compounds as the customer base grows.${commissionNote ? ` ${commissionNote}` : ''}` : null;
  const cadenceNote = inputs.revenueCadence === 'monthly' ? ' /mo' : inputs.revenueCadence === 'quarterly' ? ' /qtr' : '';
  const assumptions = [{
    label: 'Ticket',
    value: usd(inputs.ticket) + cadenceNote
  },
  // Margin plays no part in the manager view, so it's left off.
  ...(managerView(inputs) ? [] : [{
    label: commissionsModeled(inputs) ? 'Margin, pre-commission' : 'Margin',
    value: pct(inputs.gpm)
  }]), {
    label: 'Sales / veteran / mo',
    value: String(inputs.salesVetMonthly)
  }, inputs.industry === 'Summer Pest' ? {
    label: 'Sales, mo 1–3 after launch',
    value: [inputs.rampMonth1Pct ?? 25, inputs.rampMonth2Pct ?? 50, inputs.rampMonth3Pct ?? 75].map(p => String(Math.round(p / 100 * inputs.salesVetMonthly * 10) / 10)).join('/')
  } : {
    label: 'Ramp, mo 1–3',
    value: `${inputs.rampMonth1Pct ?? 25}/${inputs.rampMonth2Pct ?? 50}/${inputs.rampMonth3Pct ?? 75}%`
  }, {
    label: 'Pull-through',
    value: pct(inputs.pullthrough)
  }, {
    label: 'Ad cost per hire',
    value: `${usd(model.cphLow)}–${usd(model.cphHigh)}`
  }, {
    label: 'Hires who launch',
    value: pct(inputs.pctLaunched)
  }, {
    label: 'Day 30, of launched',
    value: pct(inputs.pct30)
  }, {
    label: 'Day 90, of day-30',
    value: pct(inputs.pct90)
  }, {
    label: 'Day 90 to 1 year',
    value: pct(inputs.pct1y)
  }];
  return <BriefPage data-id="element-480" data-mp="7krpv2j0bzy1">
      <BriefHeader label="Financial model" data-id="element-481" data-mp="7k893p53fhll" />

      <h1 className="brief-display mt-5 text-[36px] font-extrabold leading-[1.08] tracking-[-0.02em] max-w-[800px]" data-id="element-482" data-mp="7j6z7nztd15g">
        {headline}
      </h1>
      <p className="mt-2 text-[15.5px] leading-[1.45] text-[#2c3640] max-w-[800px]" data-id="element-483" data-mp="3r7toxqhuc4ac">
        {lead}
      </p>

      <section className="mt-4 rounded-xl bg-[#121a20] px-6 pt-4 pb-3.5" data-id="element-484" data-mp="3n5f26vb1wkds">
        <div className="grid grid-cols-4" data-id="element-485" data-mp="3i7wvzcof4j9h">
          {[{
          title: 'Recruiting costs',
          value: usd(summary.investment),
          note: 'Fees, ad spend and est. bonuses'
        }, {
          title: 'Gross profit',
          value: usd(summary.grossProfit),
          note: `After commissions, on ${compactUsd(summary.grossRevenue)} revenue`
        }, {
          title: 'Contribution',
          value: usd(summary.net),
          note: 'Gross profit minus recruiting costs',
          accent: true
        }, {
          title: 'Return on spend',
          value: roiMultiple(summary.roi),
          note: 'Gross profit ÷ recruiting costs',
          accent: true
        }].map((kpi, index) => <div key={kpi.title} className={index === 0 ? 'pr-5' : 'px-5 border-l border-[#33404a]'} data-id="element-486" data-mp="2pig6ams6nggy" data-mpi={index}>
              <div className={darkLabel} data-id="element-487" data-mp="1rrx2c4gwvsrz">{kpi.title}</div>
              <div className={`brief-display mt-1.5 text-[28px] font-extrabold leading-none whitespace-nowrap ${kpi.accent ? 'text-[#6fd6b0]' : 'text-white'}`} data-id="element-488" data-mp="1nt7oybvvib8c">
                {kpi.value}
              </div>
              <p className="mt-1.5 text-[12.5px] leading-snug text-[#d7dde2]" data-id="element-489" data-mp="1wmt78578yami">{kpi.note}</p>
            </div>)}
        </div>
        <div className="mt-3 pt-2.5 border-t border-[#33404a] flex items-baseline justify-between gap-6 text-[14px] text-[#d7dde2]" data-id="element-490" data-mp="14ttuzty590hu">
          <span data-id="element-491" data-mp="1iu4luwzgef30">
            <span className="font-bold text-white" data-id="element-492" data-mp="1iuwf415kw0q4">Breakeven: </span>
            {summary.breakevenLabel ?? 'Beyond 24 months'}
          </span>
          <span data-id="element-493" data-mp="2u88fu361k382">
            <span className="font-bold text-white" data-id="element-494" data-mp="2u7p8thhxiz8t">24 months: </span>
            {usd(summary.net24)} contribution, {roiMultiple(summary.roi24)}
          </span>
          <span className="text-[#6fd6b0] font-semibold" data-id="element-495" data-mp="2pbroz17gjara">
            {scenarioActive ? `Stress case: ${model.scenarioLabel.toLowerCase()}` : model.windowLabel}
          </span>
        </div>
      </section>

      <div className="mt-5 flex items-baseline justify-between gap-6" data-id="element-496" data-mp="21pyfwmilcwo">
        <h2 className={label} data-id="element-497" data-mp="701a8cwddxhj">Monthly projection · {model.windowLabel}</h2>
        <span className="text-[13px] text-[#4a5560]" data-id="element-498" data-mp="725a8jr5eccj">
          {model.leaderMonths > 0 && chain.leaderSales > 0 ? `Includes ${count(chain.leaderSales)} sales from your leader` : scenarioActive ? `Stress case: ${model.scenarioLabel.toLowerCase()}` : 'Shaded row: breakeven'}
        </span>
      </div>
      <table className="mt-1.5 w-full border-collapse text-[12.5px]" data-id="element-499" data-mp="py3pw1312i2o">
        <thead data-id="element-500" data-mp="1dohn060gz744">
          <tr className="text-[10.5px] font-bold uppercase tracking-[0.04em] text-[#4a5560] align-bottom" data-id="element-501" data-mp="1dny1kfbm20lc">
            <th className="py-1.5 pr-2 text-left font-bold" data-id="element-502" data-mp="1ifg437ta898f">Period</th>
            {COLUMNS.map(column => <th key={column.key} className={`py-1.5 px-1.5 text-right font-bold leading-tight ${column.groupStart ? 'border-l border-[#d5dade]' : ''}`} data-id="element-503" data-mp="1sa58pvmbmjp3">
                {column.key === 'grossProfit' && managerView(inputs) ? 'Override income' : column.title}
              </th>)}
          </tr>
        </thead>
        <tbody data-id="element-504" data-mp="19d41s597i9ii">
          {projection.map((line, index) => <tr key={line.label} className={`border-t border-[#e3e7ea] ${summary.breakevenMonth === index ? 'bg-[#e2f3ea]' : ''}`} data-id="element-505" data-mp="14lvq12kemc26" data-mpi={index}>
              <td className="py-[5px] pr-2 pl-1 text-left font-bold whitespace-nowrap" data-id="element-506" data-mp="zk6d1hdmfobg">
                {line.label}
                {summary.breakevenMonth === index && <span className="sr-only" data-id="element-507" data-mp="pna5pezgl88h"> (breakeven)</span>}
              </td>
              {COLUMNS.map(column => cell(line, column))}
            </tr>)}
          <tr className="border-t-2 border-[#121a20] bg-[#f1f3f4] font-bold" data-id="element-508" data-mp="2xad58stvn3mw">
            <td className="py-[6px] pr-2 pl-1 text-left whitespace-nowrap" data-id="element-509" data-mp="2xcfsx67k4kog">
              {projectionYearOne.label}
            </td>
            {COLUMNS.map(column => cell(projectionYearOne, column, true))}
          </tr>
        </tbody>
      </table>
      <p className="mt-1.5 text-[12px] leading-[1.45] text-[#4a5560]" data-id="element-510" data-mp="3b8jlyw6p2mzc">
        {residualNote && <span className="font-bold text-[#14684d]" data-id="element-511" data-mp="2dnf73qvz8r1b">{residualNote} </span>}
        {footnote}
      </p>

      <div className="mt-4 grid grid-cols-2 gap-5 items-stretch" data-id="element-512" data-mp="2ilqfdx6mithf">
        {/* The panel stretches to the stress test's height; its three parts
            spread top, middle and bottom so it never sits two-thirds full. */}
        <section className="flex flex-col rounded-xl bg-[#121a20] px-5 py-3.5" data-id="element-513" data-mp="2sctit5n0zkjv">
          <h2 className={darkLabel} data-id="element-514" data-mp="1uvcnc6o1gumu">The economics of one rep</h2>
          {rep ? <div className="mt-1.5 flex flex-1 flex-col justify-between gap-2" data-id="element-515" data-mp="1zt4q5vq8215t">
              <p className="brief-display text-[21px] font-extrabold leading-[1.25] text-white" data-id="element-516" data-mp="5fis9ktbj3wd">
                A rep who reaches day 90 costs{' '}
                <span className="text-[#6fd6b0]" data-id="element-517" data-mp="1b2l7g7yxqfk">{usd(rep.costPer90DayRep)}</span>.
              </p>
              <dl className="grid grid-cols-2 gap-x-4 border-t border-[#33404a] pt-2.5" data-id="element-518" data-mp="f652ezi9n27o">
                {[ninetyDays, yearOne].map(horizon => horizon && <div key={horizon.key} data-id="element-519" data-mp="p4ayv1lazg55">
                        <dt className="text-[12px] text-[#aab4bc]" data-id="element-520" data-mp="k2bze2sop3dh">
                          Gross profit, {horizon.key === '90d' ? 'first 90 days' : 'first 12 months'}
                        </dt>
                        <dd className="mt-0.5 flex items-baseline gap-2 whitespace-nowrap" data-id="element-521" data-mp="t54qo61v9f3b">
                          <span className="brief-display text-[22px] font-extrabold text-white" data-id="element-522" data-mp="2zvkmlbmzbsoc">
                            {usd(horizon.grossProfit)}
                          </span>
                          <span className="text-[14px] font-bold text-[#6fd6b0]" data-id="element-523" data-mp="1acvxc6jg12r9">
                            {roiMultiple(horizon.multiple * 100)}
                          </span>
                        </dd>
                      </div>)}
              </dl>
              <p className="border-t border-[#33404a] pt-2 text-[12.5px] leading-[1.4] text-[#d7dde2]" data-id="element-524" data-mp="15mfeg6wusoxi">
                One steady campaign month ({usd(rep.cohortCost)}) ÷{' '}
                {rep.cohortNinetyDayReps.toFixed(2)} day-90 reps, or{' '}
                {count(rep.hiresPer90DayRep)} hires per day-90 rep.
                {model.leaderMonths ? ' Leader search not included.' : ''}
              </p>
            </div> : <p className="mt-2 text-[13.5px] text-[#d7dde2]" data-id="element-525" data-mp="mobjcgdswqep">
              With the current retention inputs, no reps reach day 90 in year one.
            </p>}
        </section>

        <section className="flex flex-col" data-id="element-526" data-mp="2h0jbxn2ngv7b">
          <h2 className={label} data-id="element-527" data-mp="3pyrguz0hbkou">Stress test · contribution</h2>
          <ul className="mt-1.5" data-id="element-528" data-mp="3q0ca8lb24xl4">
            {model.scenarios.map(row => <li key={row.key} className="grid grid-cols-[1fr_auto] items-baseline gap-x-4 py-[7px] border-b border-[#e3e7ea]" data-id="element-529" data-mp="3kymwuz0d47tv">
                <span className="text-[14px] font-bold" data-id="element-530" data-mp="3bzjdrv173sfd">{row.label}</span>
                <span className={`brief-display text-[20px] font-extrabold leading-none whitespace-nowrap text-right ${row.summary.net >= 0 ? 'text-[#14684d]' : 'text-[#2c3640]'}`} data-id="element-531" data-mp="cjv67urphnel">
                  {usd(row.summary.net)}
                </span>
                <span className="col-span-2 mt-0.5 text-[12.5px] text-[#4a5560]" data-id="element-532" data-mp="3pghl1lad0it">
                  {roiMultiple(row.summary.roi)} return · breakeven{' '}
                  {row.summary.breakevenLabel ?? '24+ months'}
                </span>
              </li>)}
          </ul>
          <p className="mt-1.5 text-[11.5px] leading-snug text-[#4a5560]" data-id="element-533" data-mp="rfubkxnfl9g1">{stressDefinition}</p>
        </section>
      </div>

      <section className="mt-4 rounded-lg bg-[#f1f3f4] px-4 pt-2.5 pb-3" data-id="element-534" data-mp="yumsve1ot2jb">
        <h2 className={label} data-id="element-535" data-mp="13rmzbq2g87ay">Assumptions · all from your inputs</h2>
        <dl className="mt-1.5 grid grid-cols-5 gap-x-5 gap-y-1.5" data-id="element-536" data-mp="18peoicbplfbg">
          {assumptions.map(item => <div key={item.label} className="min-w-0" data-id="element-537" data-mp="1csukr7jv9dnm">
              <dt className="text-[11.5px] text-[#4a5560] whitespace-nowrap" data-id="element-538" data-mp="1hr5xbq1jq29j">{item.label}</dt>
              <dd className="text-[14px] font-bold whitespace-nowrap" data-id="element-539" data-mp="1mq8yv0ibhqcr">{item.value}</dd>
            </div>)}
        </dl>
      </section>

      <footer className="mt-auto pt-4 flex items-center justify-between text-[13px] text-[#2c3640]" data-id="element-540" data-mp="1ri0kcwmm95w1">
        <span data-id="element-541" data-mp="1weqwj8vavhc6">D2D HIRE · Financial model · {paging.label(1)}</span>
        <span data-id="element-542" data-mp="20i5p0z0fdq6i">Projections, not guarantees, except where stated</span>
      </footer>
    </BriefPage>;
}